import { intersection } from 'lodash';
import React from 'react';
import { useAuth } from 'src/hooks/useAuth';
import { BasicButton, IBasicButtonProps } from 'src/design/atoms/BasicButton';
import {
  getWorkflowPermissions,
  IWorkflowAuthorizationSource,
} from 'src/utils/workflowPermissions';

export interface IWorkflowCreateButtonProps extends IBasicButtonProps {
  workflow?: IWorkflowAuthorizationSource | null;
}

/**
 * Gates the create action by the workflow's authorization lists (strict):
 * - admin always allowed; an excluded user is denied.
 * - open workflow (no authorized roles AND no authorized users) → anyone.
 * - otherwise the user must be in a set group; an empty list is NOT a bypass.
 * @registry Authorization-gated "create" button for job workflows; renders nothing until the check passes. Strict semantics — differs from the lenient AuthorizedButton.
 */
export const WorkflowCreateButton = React.forwardRef<
  HTMLButtonElement,
  IWorkflowCreateButtonProps
>(({ workflow, ...rest }, ref) => {
  const { isAdmin, user, roleIds } = useAuth();

  if (!workflow) return null;

  const { allowedRoles, allowedUsers, blockedUsers } =
    getWorkflowPermissions(workflow);
  const userId = user?.id ?? -1;

  if (!isAdmin) {
    if (blockedUsers.includes(userId)) return null;

    const restricted = allowedRoles.length > 0 || allowedUsers.length > 0;
    if (restricted) {
      const roleMatch = intersection(allowedRoles, roleIds).length > 0;
      const userMatch = allowedUsers.includes(userId);
      if (!roleMatch && !userMatch) return null;
    }
  }

  return <BasicButton {...rest} ref={ref} />;
});
WorkflowCreateButton.displayName = 'WorkflowCreateButton';
